import styled from "styled-components";
import { Link } from "../../components/atoms/Link";
import { ExternalLinkIcon } from "../../components/atoms/ExternalLinkIcon";
import { SubHeading } from "../../components/atoms/Text";
import CompactTable, {
  leftColumnPrimaryStyle,
  leftColumnSecondaryStyle,
} from "../../components/organisms/CompactTable";
import colours from "../../lib/colours";
import Callout from "../../components/molecules/Callout";
import png1 from "../../assets/images/map-offset1.png";
import png2 from "../../assets/images/map-offset2.png";
import png3 from "../../assets/images/map-removal4.png"; // same img as in the map removal instructions
import png4 from "../../assets/images/map-offset4.png";
import png5 from "../../assets/images/map-offset5.png";
import png6 from "../../assets/images/map-offset6.png";
import {
  assessorsHandbook,
  firstPartyOffsetGuide,
  firstPartyOffsetPlanTemplate,
  gainScoringManual,
} from "../../lib/constants";

const Container = styled.div`
  display: block;
  font-weight: 300;
`;

const ContentSubHeading = styled(SubHeading)`
  font-size: 17px;
`;

const MediumSpan = styled.span`
  font-weight: 500;
`;

const TableTextContainer = styled.div`
  color: ${colours.darkerGrey};
  font-weight: 300;
  font-size: 16px;
`;

const NoMarginP = styled.p`
  margin: 0;
`;

const ItalicP = styled.p`
  font-style: italic;
`;

const Span16 = styled.span`
  font-size: 16px;
`;

const TableTwoColumnContainer = styled(TableTextContainer)`
  display: flex;
`;

const StyledImage = styled.img`
  min-width: 40%;
  max-width: 40%;
  object-fit: cover;
`;

const SpacedLi = styled.li`
  margin-block: 8px;
`;

const InsetDiv = styled.div`
  padding-left: 22px;
`;

const twoColumnContentStyle: any = { ...leftColumnSecondaryStyle };
twoColumnContentStyle.padding = 0;
twoColumnContentStyle.paddingLeft = 0;
twoColumnContentStyle.paddingBlock = "18px 36px";

const patchDescription = (
  <TableTextContainer>
    <NoMarginP>
      A <MediumSpan>Patch</MediumSpan> can consist of one, or a combination of
      the following:
    </NoMarginP>
    <ul>
      <li>
        An area of vegetation where native plants make up at least 25% of the
        total perennial
        <MediumSpan style={{ color: colours.th1l3alt }}>*</MediumSpan>{" "}
        understorey plant cover.
      </li>
      <li>
        Any area that contains three or more{" "}
        <MediumSpan>Canopy Trees</MediumSpan> with overlapping driplines.
      </li>
      <li>
        An area located within a Mapped Wetland, provided that native plants
        make up at least 25% of the total perennial understorey plant cover.
      </li>
    </ul>
    <p>Notes:</p>
    <ItalicP>
      <MediumSpan style={{ color: colours.th1l3alt }}>*</MediumSpan> Perennial
      plants have a lifespan of over two years and encompass species that may or
      may not be consistently visible above the ground.
    </ItalicP>
    <Callout type="info" backgroundColor={colours.offWhite}>
      An example of identifying areas where at least 25% of the perennial
      understorey plant cover is native:
      <br />
      <br />
      An area is composed of 80% weedy annual grasses, 15% native perennial
      plants, and 5% weedy perennial plants. Disregarding the cover provided by
      the annual weeds (80%), the remaining plant cover consists of perennial
      plants, accounting for 20%. Within this perennial plant cover, 75% (15/20)
      comprises native species, indicating the presence of a Patch.
    </Callout>
    <br />
  </TableTextContainer>
);

const canopyTreeDescription = (
  <TableTextContainer>
    <NoMarginP>
      A <MediumSpan>Canopy Tree</MediumSpan> refers to a mature tree, exceeding
      a height of 3 meters, capable of flowering, and commonly found in the
      upper layer of the respective vegetation type. These trees can be present
      within a <MediumSpan>Patch</MediumSpan> of native vegetation (referred to
      as <MediumSpan>Patch Tree</MediumSpan>) or occur independently (known as a{" "}
      <MediumSpan>Scattered Tree</MediumSpan>). Additionally, a{" "}
      <MediumSpan>Canopy Tree</MediumSpan> can also encompass a dead tree with a
      diameter of 40 cm or larger.
    </NoMarginP>
  </TableTextContainer>
);

const revegetationDescription = (
  <TableTextContainer>
    <NoMarginP>
      An area of revegetation must be within a woody vegetation type and meet
      the minimum area to perimeter ratio requirements. NVR Map will alert you
      if these requirements are not met.
    </NoMarginP>
    <p>
      Revegetation must adhere to the minimum planting standards specified in
      the{" "}
      <Link
        href={gainScoringManual}
        target="_blank"
        title="Native vegetation gain scoring manual, Version 2"
        rel="noopener noreferrer"
      >
        Native vegetation gain scoring manual, Version 2
      </Link>
      <ExternalLinkIcon />. The first party general offset site management plan{" "}
      <Link
        href={firstPartyOffsetPlanTemplate}
        target="_blank"
        title="First party general offset site management plan template"
        rel="noopener noreferrer"
      >
        template
      </Link>
      <ExternalLinkIcon /> on the DEECA website will assist you in meeting these
      standards.
    </p>
  </TableTextContainer>
);

const scenariosContent = [
  <div>
    <ul>
      <SpacedLi>
        An area of vegetation where native plants make up at least 25% of the
        total perennial understorey plant cover.
      </SpacedLi>
    </ul>
  </div>,
  <div>
    <ul>
      <SpacedLi>
        An area of vegetation where native plants make up at least 25% of the
        total perennial understorey plant cover.
      </SpacedLi>
      <SpacedLi>
        The Patch boundary must encompass the canopy/drip line of any impacted
        Patch Trees.
      </SpacedLi>
    </ul>
  </div>,
  <div>
    <ul>
      <SpacedLi>
        An area with three or more Canopy Trees where the drip line of each tree
        touches the drip line of at least one other tree, forming a continuous
        canopy cover.
      </SpacedLi>
      <SpacedLi>
        The total perennial understorey plant cover is &lt; 25% native.
      </SpacedLi>
      <SpacedLi>
        The boundary of the Patch must encompass the canopy/drip line of the
        trees.
      </SpacedLi>
    </ul>
  </div>,
  <div>
    <ul>
      <SpacedLi>A Scattered Tree(s) proposed for removal.</SpacedLi>
      <SpacedLi>
        The total perennial understorey plant cover is &lt; 25% native.
      </SpacedLi>
      <SpacedLi>
        Includes dead trees with a diameter of 40cm or greater.
      </SpacedLi>
    </ul>
    <InsetDiv>
      <p style={{ marginBottom: 0 }}>Note:</p>
      <ItalicP style={{ marginTop: "6px" }}>
        The polygon extends beyond the dripline of the tree(s). The area of the
        polygon, rather than the individual tree(s), is used to calculate gains.
      </ItalicP>
    </InsetDiv>
  </div>,
  <div>
    <ul>
      <SpacedLi>
        An area of revegetation with a woody Ecological Vegetation Class (EVC).
      </SpacedLi>
    </ul>
  </div>,
  <div>
    <ul>
      <SpacedLi>
        An area of revegetation with a woody Ecological Vegetation Class (EVC).
      </SpacedLi>
      <SpacedLi>Revegetation is proposed around a Scattered Tree.</SpacedLi>
    </ul>
  </div>,
];

const ScenarioLayout = ({
  imgSource,
  imgAlt,
  children,
}: {
  imgSource: any;
  imgAlt: string;
  children: any;
}) => {
  return (
    <TableTwoColumnContainer>
      <StyledImage src={imgSource} alt={imgAlt} title={imgAlt} />
      {children}
    </TableTwoColumnContainer>
  );
};

function HowToMapOffset() {
  const definitionRows: any = [
    {
      rowData: [<Span16 key={0}>Patch</Span16>],
      rowStyles: [leftColumnPrimaryStyle],
    },
    {
      rowData: [<span key={1}>{patchDescription}</span>],
      rowStyles: [leftColumnSecondaryStyle],
    },
    {
      rowData: [
        <Span16 key={2}>Canopy Tree (Patch Tree and Scattered Tree)</Span16>,
      ],
      rowStyles: [leftColumnPrimaryStyle],
    },
    {
      rowData: [<span key={3}>{canopyTreeDescription}</span>],
      rowStyles: [leftColumnSecondaryStyle],
    },
    {
      rowData: [<Span16 key={4}>Revegetation</Span16>],
      rowStyles: [leftColumnPrimaryStyle],
    },
    {
      rowData: [<span key={5}>{revegetationDescription}</span>],
      rowStyles: [leftColumnSecondaryStyle],
    },
  ];

  const scenarioRows: any = [
    {
      rowData: [<Span16 key={0}>1. Patch without Patch Trees</Span16>],
      rowStyles: [leftColumnPrimaryStyle],
    },
    {
      rowData: [
        <ScenarioLayout
          key={1}
          imgSource={png1}
          imgAlt="Example of Patch without Patch Trees"
        >
          {scenariosContent[0]}
        </ScenarioLayout>,
      ],
      rowStyles: [twoColumnContentStyle],
    },
    {
      rowData: [<Span16 key={2}>2. Patch with Patch Trees</Span16>],
      rowStyles: [leftColumnPrimaryStyle],
    },
    {
      rowData: [
        <ScenarioLayout
          key={3}
          imgSource={png2}
          imgAlt="Example of Patch with Patch Trees"
        >
          {scenariosContent[1]}
        </ScenarioLayout>,
      ],
      rowStyles: [twoColumnContentStyle],
    },
    {
      rowData: [
        <Span16 key={4}>
          3. Patch without native understorey (3 or more trees only)
        </Span16>,
      ],
      rowStyles: [leftColumnPrimaryStyle],
    },
    {
      rowData: [
        <ScenarioLayout
          key={5}
          imgSource={png3}
          imgAlt="Example of Patch with Patch Trees overhanging the edge of the Patch"
        >
          {scenariosContent[2]}
        </ScenarioLayout>,
      ],
      rowStyles: [twoColumnContentStyle],
    },
    {
      rowData: [<Span16 key={6}>4. Scattered Tree(s)</Span16>],
      rowStyles: [leftColumnPrimaryStyle],
    },
    {
      rowData: [
        <ScenarioLayout
          key={7}
          imgSource={png4}
          imgAlt="Example of Patch without native understorey"
        >
          {scenariosContent[3]}
        </ScenarioLayout>,
      ],
      rowStyles: [twoColumnContentStyle],
    },
    {
      rowData: [<Span16 key={8}>5. Revegetation</Span16>],
      rowStyles: [leftColumnPrimaryStyle],
    },
    {
      rowData: [
        <ScenarioLayout
          key={9}
          imgSource={png5}
          imgAlt="Example of Patch including Mapped Wetland"
        >
          {scenariosContent[4]}
        </ScenarioLayout>,
      ],
      rowStyles: [twoColumnContentStyle],
    },
    {
      rowData: [
        <Span16 key={10}>6. Revegetation around Scattered Tree(s)</Span16>,
      ],
      rowStyles: [leftColumnPrimaryStyle],
    },
    {
      rowData: [
        <ScenarioLayout
          key={11}
          imgSource={png6}
          imgAlt="Example of Scattered Trees"
        >
          {scenariosContent[5]}
        </ScenarioLayout>,
      ],
      rowStyles: [twoColumnContentStyle],
    },
  ];

  const FirstPartyOffsetGuideLink = (
    <>
      <Link
        href={firstPartyOffsetGuide}
        target="_blank"
        title="First Party Offset Guide"
        rel="noopener noreferrer"
      >
        First Party Offset Guide
      </Link>
      <ExternalLinkIcon />
    </>
  );

  return (
    <Container>
      <p>
        For detailed information on how to define and map native vegetation,
        refer to the following resources:
      </p>
      <ul>
        <li>
          {FirstPartyOffsetGuideLink} – Appendices 1 and 2 provide valuable
          information.
        </li>
        <li>
          <Link
            href={assessorsHandbook}
            target="_blank"
            title="Assessor’s Handbook"
            rel="noopener noreferrer"
          >
            Assessor’s Handbook
          </Link>
          <ExternalLinkIcon /> – Appendices 1 and 6 offer useful guidance.
        </li>
      </ul>
      <p>
        The images below highlight typical first party offset mapping scenarios.
        Your proposal can encompass any single scenario or a combination
        thereof.
      </p>
      <p>
        To ensure successful mapping of your proposed offset site, it is highly
        recommended that you review the {FirstPartyOffsetGuideLink}. This
        document serves as a valuable resource, offering step-by-step guidance
        on establishing a first-party offset. It also provides crucial
        information to help you assess whether your proposal meets the necessary
        eligibility requirements.
      </p>
      <div style={{ marginTop: "36px" }}>
        <ContentSubHeading>Definitions</ContentSubHeading>
        <p>
          The following features can be secured as part of a first party offset,
          with the minimum condition, size and configuration requirements
          outlined in Appendix 2 of the {FirstPartyOffsetGuideLink}.
        </p>
        <CompactTable
          rows={definitionRows}
          getRowKey={(row: any) => row[0].key}
        ></CompactTable>
        <ContentSubHeading>
          Common first party offset mapping scenarios
        </ContentSubHeading>
        <CompactTable
          rows={scenarioRows}
          getRowKey={(row: any) => row[0].key}
        ></CompactTable>
      </div>
    </Container>
  );
}

export default HowToMapOffset;
